import { ReactNode, useMemo } from "react";
import { Control, FieldError, FieldPath, FieldValues } from "react-hook-form";
import { useIntl } from "react-intl";

import AsyncSelect, { Option } from "@components/fields/AsyncSelect";
import useAsyncSearch from "@hooks/useAsyncSearch";
import useLazyQueryWithPromise from "@hooks/useLazyQueryWithPromise";
import { employerSelectSelfPaid } from "@typing/Constants";
import { Employer, GetEmployersDocument, GetEmployersQuery, GetEmployersQueryVariables } from "@typing/Generated";

type Props<FormData extends FieldValues> = {
  allowAny?: boolean;
  allowSelfPaid?: boolean;
  anyOptionLabelKey?: string;
  control: Control<FormData>;
  disabled?: boolean;
  error: FieldError | undefined;
  initialOption: {
    label: string | undefined;
    value: string | null;
  };
  label?: string | ReactNode;
  name: FieldPath<FormData>;
  onSelection?: (option: { label: string; value: string }) => void;
  required?: boolean;
};

const EmployerSelect = <FormData extends FieldValues>({
  allowAny = true,
  allowSelfPaid = true,
  anyOptionLabelKey,
  control,
  disabled = false,
  error,
  initialOption,
  label,
  name,
  onSelection,
  required = false
}: Props<FormData>) => {
  const intl = useIntl();

  const getEmployers = useLazyQueryWithPromise<GetEmployersQuery, GetEmployersQueryVariables>(GetEmployersDocument);

  const defaultOptions = useMemo(() => {
    const options = [];
    if (allowAny) {
      options.push({ label: intl.formatMessage({ id: anyOptionLabelKey ?? "dictionary.any" }), value: "" });
    }
    if (allowSelfPaid) {
      options.push({
        label: intl.formatMessage({ id: "dictionary.selfPaid" }),
        value: employerSelectSelfPaid
      });
    }

    return options;
  }, [allowAny, allowSelfPaid, anyOptionLabelKey, intl]);

  const employerAsyncSearch = useAsyncSearch<Employer>({
    defaultOptions,
    getLabel: t => t?.name ?? "",
    getValue: t => t?.id ?? "",
    initialOption
  });

  return (
    <AsyncSelect<FormData>
      control={control}
      defaultOptions={employerAsyncSearch.defaultOptions}
      disabled={disabled}
      error={error}
      label={label}
      name={name}
      onSearch={async search =>
        employerAsyncSearch.searchOptions(search, getEmployers({ variables: { searchTerm: search } }))
      }
      onSelection={(option: Option) => {
        employerAsyncSearch.setCurrentOption(option);
        onSelection?.(option);
      }}
      required={required}
      value={employerAsyncSearch.currentOption}
    />
  );
};

export default EmployerSelect;
